import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTypes, Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import {
	getListApplicationRegistrationsGroupedQueryKey,
	useCreateApplicationRegistration,
	useCreateCompositeWithApplicationRegistration,
} from '@/generated/application-registrations';
import { getListApplicationRegistrationGroupsQueryKey } from '@/generated/application-registrations';
import { AccountId, ApplicationRegistrationRequest, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './ApplicationDetail.module.scss';
import { ApplicationForm } from './shared/ApplicationForm';
import { useRotation } from './shared/useRotation';

interface RegisterApplicationProps {
	accountId: AccountId;
	registrationCompositeId?: number;
}

export const RegisterApplication: React.FunctionComponent<RegisterApplicationProps> = ({
	accountId,
	registrationCompositeId,
}) => {
	const formId = 'register-application-form'; // used for binding form and submit button
	const { segmentationId } = useContext(SegmentationContext);
	const { data: layout } = useGetLayout(segmentationId);
	const { data: translations } = useGetTranslations(segmentationId);
	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

	const [hasFormChanged, setHasFormChanged] = useState(false);

	const path = useMemo(() => {
		return layout?.data.staticLinks.find((link) => link.pageType === PageType.RegistrationComposite)?.url;
	}, [layout]);

	const ariaControlId = 'register-application';
	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const innerBody = useRef<HTMLDivElement>(null);

	const { desktopWidth } = useRotation(innerBody?.current, isOpen);

	// shared options used for create application
	const mutationOptions = {
		onSuccess: async (x) => {
			// refresh application registration grouped list query cache
			await queryClient.invalidateQueries({
				queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
			});

			// refresh groups list if new group is added
			await queryClient.invalidateQueries({
				queryKey: getListApplicationRegistrationGroupsQueryKey(segmentationId, accountId),
			});

			addNotification({
				children: translations?.data.applicationRegistrations.saveSuccess,
				type: AlertTypes.SUCCESS,
			});

			if (!registrationCompositeId) {
				window.location.href = `${path}?accountid=${accountId}&registrationcompositeid=${x.data}`;
			} else {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});
			}

			handleClose();
		},
		onError: (axiosError) => {
			const error = axiosError.response?.data;
			if (!error) {
				console.error('Unknown error', axiosError);
			} else if (isHttpValidationProblemDetails(error)) {
				addNotification({
					children: translations?.data.applicationRegistrations.saveFailed,
					type: AlertTypes.DANGER,
				});

				setValidationErrors(error.errors);
			} else {
				addNotification({
					children: translations?.data.shared.genericErrorMsg,
					type: AlertTypes.DANGER,
				});
			}
		},
	};
	const createApplicationRegistrationMutation = useCreateApplicationRegistration({
		mutation: mutationOptions,
	});

	const createCompositeWithApplicationRegistrationMutation = useCreateCompositeWithApplicationRegistration({
		mutation: mutationOptions,
	});

	const openDrawer = useCallback((): void => {
		setHasFormChanged(false);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const formData = new FormData(event.target);

		setValidationErrors(null);

		const applicationRegistrationForm: ApplicationRegistrationRequest = {
			name: String(formData.get('name')) || '',
			segmentSystemName: String(formData.get('segment')) || '',
			applicationSystemName: String(formData.get('application')) || '',
			brand: String(formData.get('brand')),
			model: String(formData.get('model')),
			fluidType: String(formData.get('fluidType')),
			fluidBrand: String(formData.get('fluidBrand')),
			oilSystem: String(formData.get('oilSystem')),
			oilSystemBrand: String(formData.get('oilSystemBrand')),
			powerSupply: String(formData.get('powerSupply')),
			viscosity: formData.get('viscosity') === '' ? null : Number(formData.get('viscosity')),
			systemVolume: formData.get('systemVolume') === '' ? null : Number(formData.get('systemVolume')),
			minAmbientTemperature:
				formData.get('minAmbientTemperature') === '' ? null : Number(formData.get('minAmbientTemperature')),
			maxAmbientTemperature:
				formData.get('maxAmbientTemperature') === '' ? null : Number(formData.get('maxAmbientTemperature')),
			minOperationTemperature:
				formData.get('minOperationTemperature') === '' ? null : Number(formData.get('minOperationTemperature')),
			maxOperationTemperature:
				formData.get('maxOperationTemperature') === '' ? null : Number(formData.get('maxOperationTemperature')),
			normalOperationTemperature:
				formData.get('normalOperationTemperature') === ''
					? null
					: Number(formData.get('normalOperationTemperature')),
			additionalInformation: String(formData.get('additionalInformation')),
		};

		if (registrationCompositeId) {
			createApplicationRegistrationMutation.mutate({
				segmentationId,
				accountId,
				data: applicationRegistrationForm,
				registrationCompositeId,
			});
		} else {
			createCompositeWithApplicationRegistrationMutation.mutate({
				segmentationId,
				accountId,
				data: applicationRegistrationForm,
			});
		}
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				onClick={openDrawer}
				aria-expanded={isOpen}
				variant={'ghost'}
				aria-controls={ariaControlId}
				aria-label={translations?.data.applications.registerApplication}
			>
				<Icon name={'plus'} size={'sm'} />
			</Button>

			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={`${desktopWidth}px`}
					direction={'Right'}
				>
					<Drawer.Header
						title={translations?.data.applications.registerApplication}
						handleClose={handleClose}
					/>

					<Drawer.Body>
						<div className={styles.innerBody} ref={innerBody}>
							<ApplicationForm
								formId={formId}
								legend={translations?.data.applications.registerApplication}
								isOpen={isOpen}
								validationErrors={validationErrors}
								handleSubmit={handleSubmit}
								handleChange={handleChange}
								isFullHeight={desktopWidth === 720}
							/>
						</div>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
							{translations?.data.applicationRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};
